/**
 * Tiers — every plan's full configuration, and the switch that retires one.
 *
 * The toggle flips `is_selectable`, which controls whether NEW buyers can pick
 * the plan. It deliberately does NOT touch existing subscriptions: retiring a
 * plan must not cancel the people already paying for it. That is why each card
 * shows its live subscriber count — it is the number you want in front of you
 * before switching a plan off.
 */
import { useCallback, useEffect, useState } from "react";
import { Check, Clock, CreditCard, Loader2, Lock, Pencil, Plus, Tag, Users, X } from "lucide-react";

import { PageHero } from "@/components/interior-page";

import { superAdminApi } from "./session";

interface Tier {
  id: number;
  name: string;
  amount_per_month: number;
  amount_per_year: number | null;
  free_listings_access: number | null;
  listing_locks_allowed: number | null;
  daily_message_limit: number | null;
  advance_access_hours: number;
  lock_duration_hours: number;
  extra_listing_amount: number | null;
  requires_support_call: boolean;
  is_selectable: boolean;
  features_text: string | null;
  created_at: string;
  updated_at: string;
  active_subscribers: number;
  total_subscribers: number;
  promo_code_count: number;
}

// Tier amounts are stored in WHOLE DOLLARS, not cents — payments.py multiplies
// by 100 on its way to Stripe. Dividing here would show Explorer as $0.79.
const money = (dollars: number | null) =>
  dollars == null ? "—" : `$${dollars.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const orUnlimited = (n: number | null) => (n == null ? "Unlimited" : String(n));

type FormState = {
  id: number | null;
  name: string;
  amount_per_month: string;
  amount_per_year: string;
  free_listings_access: string;
  listing_locks_allowed: string;
  daily_message_limit: string;
  advance_access_hours: string;
  lock_duration_hours: string;
  extra_listing_amount: string;
  requires_support_call: boolean;
  is_selectable: boolean;
  features_text: string;
};

const EMPTY_FORM: FormState = {
  id: null, name: "", amount_per_month: "0", amount_per_year: "",
  free_listings_access: "", listing_locks_allowed: "", daily_message_limit: "",
  advance_access_hours: "0", lock_duration_hours: "0", extra_listing_amount: "",
  requires_support_call: false, is_selectable: true, features_text: "",
};

const formFrom = (t: Tier): FormState => ({
  id: t.id,
  name: t.name,
  amount_per_month: String(t.amount_per_month),
  amount_per_year: t.amount_per_year == null ? "" : String(t.amount_per_year),
  free_listings_access: t.free_listings_access == null ? "" : String(t.free_listings_access),
  listing_locks_allowed: t.listing_locks_allowed == null ? "" : String(t.listing_locks_allowed),
  daily_message_limit: t.daily_message_limit == null ? "" : String(t.daily_message_limit),
  advance_access_hours: String(t.advance_access_hours),
  lock_duration_hours: String(t.lock_duration_hours),
  extra_listing_amount: t.extra_listing_amount == null ? "" : String(t.extra_listing_amount),
  requires_support_call: t.requires_support_call,
  is_selectable: t.is_selectable,
  features_text: t.features_text ?? "",
});

/** Blank optional numbers mean "unlimited"/"not set". Sending null would read as
 *  "leave unchanged" on a PATCH, so they go in clear_fields instead. */
const NULLABLE = [
  "amount_per_year", "free_listings_access", "listing_locks_allowed",
  "daily_message_limit", "extra_listing_amount",
] as const;

function NumField({
  label, hint, value, onChange,
}: { label: string; hint?: string; value: string; onChange: (v: string) => void }) {
  return (
    <label className="block">
      <span className="text-[0.6rem] font-bold uppercase tracking-[0.14em] text-muted-foreground">{label}</span>
      <input
        type="number" min={0} value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 h-10 w-full rounded-xl border border-border/70 bg-background px-3 text-sm focus:border-primary focus:outline-none"
      />
      {hint && <span className="mt-1 block text-[0.65rem] text-muted-foreground">{hint}</span>}
    </label>
  );
}

function Detail({ label, value, icon: Icon }: { label: string; value: string; icon?: typeof Users }) {
  return (
    <div className="rounded-xl border border-border/70 bg-muted/30 p-3">
      <div className="flex items-center gap-1.5 text-[0.6rem] font-bold uppercase tracking-[0.14em] text-muted-foreground">
        {Icon && <Icon className="h-3 w-3" />}
        {label}
      </div>
      <div className="mt-1 text-sm font-semibold text-foreground">{value}</div>
    </div>
  );
}

export default function SuperAdminTiersPage() {
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setTiers(await superAdminApi.get<Tier[]>("/super-admin/tiers"));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load tiers.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const save = async () => {
    if (!form || saving) return;
    if (!form.name.trim()) { setError("A plan name is required."); return; }
    setSaving(true);
    setError(null);
    try {
      const body: Record<string, unknown> = {
        name: form.name.trim(),
        amount_per_month: Number(form.amount_per_month) || 0,
        advance_access_hours: Number(form.advance_access_hours) || 0,
        lock_duration_hours: Number(form.lock_duration_hours) || 0,
        requires_support_call: form.requires_support_call,
        is_selectable: form.is_selectable,
        features_text: form.features_text.trim() || null,
      };
      const clear: string[] = [];
      for (const key of NULLABLE) {
        const raw = form[key].trim();
        if (raw === "") clear.push(key);
        else body[key] = Number(raw);
      }

      if (form.id == null) {
        // POST takes plain nulls — there is no "unchanged" to distinguish from.
        for (const key of clear) body[key] = null;
        await superAdminApi.post<Tier>("/super-admin/tiers", body);
      } else {
        body.clear_fields = clear;
        try {
          await superAdminApi.patch<Tier>(`/super-admin/tiers/${form.id}`, body);
        } catch (e) {
          // 409 on a rename = the API is warning that app code matches this
          // plan by name. Surface it and let them confirm rather than retrying
          // silently behind their back.
          const msg = e instanceof Error ? e.message : "";
          if (msg.includes("acknowledge_feature_gate")) {
            if (!window.confirm(`${msg}\n\nRename anyway?`)) throw new Error("Rename cancelled.");
            body.acknowledge_feature_gate = true;
            await superAdminApi.patch<Tier>(`/super-admin/tiers/${form.id}`, body);
          } else throw e;
        }
      }
      setForm(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save the tier.");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (t: Tier) => {
    const next = !t.is_selectable;
    if (!next && t.active_subscribers > 0 && !window.confirm(
      `Deactivate ${t.name}?\n\n${t.active_subscribers} active subscriber${t.active_subscribers === 1 ? " keeps" : "s keep"} ` +
      `their plan — this only hides it from new buyers on Choose Plan. You can switch it back on at any time.`,
    )) return;
    setSavingId(t.id);
    setError(null);
    try {
      const updated = await superAdminApi.patch<Tier>(`/super-admin/tiers/${t.id}`, { is_selectable: next });
      setTiers((rows) => rows.map((r) => (r.id === updated.id ? updated : r)));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not update the tier.");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <main className="w-full space-y-6 px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
      <PageHero
        eyebrow="Billing"
        icon={CreditCard}
        title="Subscription"
        titleAccent="Tiers"
        description="Every plan with its full configuration. Deactivating hides a plan from new buyers on Choose Plan; existing subscribers keep theirs."
        actions={
          <button
            type="button"
            onClick={() => setForm({ ...EMPTY_FORM })}
            className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground shadow-sm transition-[filter] hover:brightness-105"
          >
            <Plus className="h-4 w-4" /> New tier
          </button>
        }
      />

      {error && (
        <p className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm font-medium text-rose-600">
          {error}
        </p>
      )}

      {loading ? (
        <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading tiers…
        </div>
      ) : (
        <div className="space-y-4">
          {tiers.map((t) => (
            <section key={t.id} className="rounded-2xl border border-border/80 bg-card p-5 shadow-sm sm:p-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="flex flex-wrap items-center gap-2.5">
                    <h2 className="text-xl font-bold text-foreground">{t.name}</h2>
                    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[0.65rem] font-bold uppercase tracking-[0.1em] ${
                      t.is_selectable ? "bg-emerald-500/15 text-emerald-600" : "bg-muted text-muted-foreground"
                    }`}>
                      {t.is_selectable ? "Active" : "Deactivated"}
                    </span>
                    {t.requires_support_call && (
                      <span className="rounded-full bg-primary/10 px-2.5 py-1 text-[0.65rem] font-bold uppercase tracking-[0.1em] text-primary">
                        Support call
                      </span>
                    )}
                  </div>
                  <p className="mt-1.5 text-sm text-muted-foreground">
                    {money(t.amount_per_month)}/mo
                    {t.amount_per_year != null && ` · ${money(t.amount_per_year)}/yr`}
                  </p>
                </div>

                <div className="flex shrink-0 items-center gap-2">
                <button
                  type="button"
                  title="Edit plan"
                  onClick={() => setForm(formFrom(t))}
                  className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  role="switch"
                  aria-checked={t.is_selectable}
                  aria-label={`Toggle ${t.name}`}
                  onClick={() => void toggle(t)}
                  disabled={savingId === t.id}
                  className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors duration-200 disabled:opacity-60 ${
                    t.is_selectable ? "bg-primary" : "bg-muted-foreground/30"
                  }`}
                >
                  <span className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition-transform duration-200 ${
                    t.is_selectable ? "translate-x-5" : "translate-x-0.5"
                  }`} />
                </button>
                </div>
              </div>

              <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <Detail label="Active subscribers" value={String(t.active_subscribers)} icon={Users} />
                <Detail label="Total ever" value={String(t.total_subscribers)} icon={Users} />
                <Detail label="Promo codes" value={String(t.promo_code_count)} icon={Tag} />
                <Detail label="Free listing unlocks" value={orUnlimited(t.free_listings_access)} icon={CreditCard} />
                <Detail label="Listing locks" value={orUnlimited(t.listing_locks_allowed)} icon={Lock} />
                <Detail label="Lock duration" value={`${t.lock_duration_hours}h`} icon={Clock} />
                <Detail label="Advance access" value={`${t.advance_access_hours}h`} icon={Clock} />
                <Detail label="Daily messages" value={orUnlimited(t.daily_message_limit)} />
                <Detail label="Extra listing price" value={money(t.extra_listing_amount)} icon={CreditCard} />
                <Detail label="Tier id" value={`#${t.id}`} />
                <Detail label="Created" value={new Date(t.created_at).toLocaleDateString()} />
                <Detail label="Updated" value={new Date(t.updated_at).toLocaleDateString()} />
              </div>

              {t.features_text && (
                <div className="mt-4 rounded-xl border border-border/70 bg-muted/30 p-3">
                  <div className="text-[0.6rem] font-bold uppercase tracking-[0.14em] text-muted-foreground">Features</div>
                  <p className="mt-1 whitespace-pre-wrap text-sm leading-6 text-foreground">{t.features_text}</p>
                </div>
              )}
            </section>
          ))}

          {form && (
            <div
              className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
              onClick={() => !saving && setForm(null)}
            >
              <div
                className="flex max-h-[88dvh] w-full max-w-2xl flex-col rounded-2xl border border-border bg-card shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex shrink-0 items-start justify-between gap-3 border-b border-border/70 px-6 py-4">
                  <h2 className="text-lg font-bold text-foreground">
                    {form.id == null ? "New tier" : `Edit ${form.name}`}
                  </h2>
                  <button type="button" onClick={() => setForm(null)} aria-label="Close"
                    className="rounded-lg p-1 text-muted-foreground hover:bg-accent hover:text-foreground">
                    <X className="h-4 w-4" />
                  </button>
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
                  <label className="block">
                    <span className="text-[0.6rem] font-bold uppercase tracking-[0.14em] text-muted-foreground">Plan name</span>
                    <input
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      maxLength={64}
                      placeholder="Operator"
                      className="mt-1.5 h-10 w-full rounded-xl border border-border/70 bg-background px-3 text-sm focus:border-primary focus:outline-none"
                    />
                  </label>

                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    <NumField label="Price / month ($)" value={form.amount_per_month}
                      onChange={(v) => setForm({ ...form, amount_per_month: v })} />
                    <NumField label="Price / year ($)" hint="Blank = no annual option"
                      value={form.amount_per_year} onChange={(v) => setForm({ ...form, amount_per_year: v })} />
                    <NumField label="Free listing unlocks" hint="Blank = unlimited"
                      value={form.free_listings_access} onChange={(v) => setForm({ ...form, free_listings_access: v })} />
                    <NumField label="Listing locks" hint="Blank = unlimited"
                      value={form.listing_locks_allowed} onChange={(v) => setForm({ ...form, listing_locks_allowed: v })} />
                    <NumField label="Daily messages" hint="Blank = unlimited"
                      value={form.daily_message_limit} onChange={(v) => setForm({ ...form, daily_message_limit: v })} />
                    <NumField label="Extra listing price ($)" hint="Blank = not offered"
                      value={form.extra_listing_amount} onChange={(v) => setForm({ ...form, extra_listing_amount: v })} />
                    <NumField label="Advance access (hours)" value={form.advance_access_hours}
                      onChange={(v) => setForm({ ...form, advance_access_hours: v })} />
                    <NumField label="Lock duration (hours)" value={form.lock_duration_hours}
                      onChange={(v) => setForm({ ...form, lock_duration_hours: v })} />
                  </div>

                  <label className="mt-4 block">
                    <span className="text-[0.6rem] font-bold uppercase tracking-[0.14em] text-muted-foreground">Features text</span>
                    <textarea
                      rows={4}
                      value={form.features_text}
                      onChange={(e) => setForm({ ...form, features_text: e.target.value })}
                      className="mt-1.5 w-full resize-none rounded-xl border border-border/70 bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
                    />
                  </label>

                  <div className="mt-4 space-y-2">
                    <label className="flex items-center gap-2 text-sm text-foreground">
                      <input type="checkbox" checked={form.requires_support_call}
                        onChange={(e) => setForm({ ...form, requires_support_call: e.target.checked })} />
                      Requires a support call before signup
                    </label>
                    <label className="flex items-center gap-2 text-sm text-foreground">
                      <input type="checkbox" checked={form.is_selectable}
                        onChange={(e) => setForm({ ...form, is_selectable: e.target.checked })} />
                      Active — new buyers can choose this plan
                    </label>
                  </div>

                  <p className="mt-4 rounded-xl border border-border/70 bg-muted/40 px-3 py-2 text-xs leading-5 text-muted-foreground">
                    Prices are whole dollars. A price change applies to new checkouts —
                    subscriptions already live in Stripe keep the price they were created with.
                  </p>
                </div>

                <div className="flex shrink-0 items-center justify-end gap-2 border-t border-border/70 px-6 py-4">
                  <button type="button" onClick={() => setForm(null)}
                    className="rounded-xl border border-border/80 px-4 py-2 text-sm font-semibold text-muted-foreground hover:bg-accent">
                    Cancel
                  </button>
                  <button type="button" onClick={() => void save()} disabled={saving}
                    className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2 text-sm font-bold text-primary-foreground disabled:opacity-60">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                    {saving ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {tiers.length === 0 && (
            <div className="rounded-2xl border border-dashed border-border/80 p-10 text-center">
              <Check className="mx-auto h-8 w-8 text-muted-foreground/40" />
              <p className="mt-3 font-semibold text-foreground">No tiers configured</p>
            </div>
          )}
        </div>
      )}
    </main>
  );
}
